import React, { useState, useRef } from 'react';
import { CancelOutlined, EditOutlined, UploadFile } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import { motion } from 'framer-motion';

export default function KYcForm({ open, handleClose, handleChange, kyc, handleSave }: any) {
	const [loading, setLoading] = useState(false);
	const gstFileRef = useRef<HTMLInputElement>(null);
	const companyFileRef = useRef<HTMLInputElement>(null);

	const handleSubmit = async () => {
		try {
			setLoading(true);

			const formData = new FormData();
			formData.append('gstNumber', kyc.gstNumber || '');
			formData.append('accountNumber', kyc.accountNumber || '');
			formData.append('accountHolder', kyc.accountHolder || '');
			formData.append('accountType', kyc.accountType || '');
			formData.append('bankName', kyc.bankName || '');
			formData.append('branch', kyc.branch || '');
			formData.append('ifscCode', kyc.ifscCode || '');

			if (kyc.gstDocumentFile) formData.append('gstDocument', kyc.gstDocumentFile);
			if (kyc.companyDocumentFile)
				formData.append('companyRegistrationDocument', kyc.companyDocumentFile);
			const response = await handleSave(formData);
			console.log(response);
		} catch (error) {
			console.error('Error updating KYC:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			PaperProps={{ sx: { borderRadius: 3 } }}
		>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.2 }}
			>
				<DialogTitle
					sx={{
						backgroundColor: '#f5f5f5',
						borderBottom: '1px solid #e0e0e0',
						fontWeight: 'bold',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Box display="flex" alignItems="center">
						<EditOutlined sx={{ mr: 1, color: '#2e7d32' }} />
						Edit KYC Details
					</Box>
					<IconButton onClick={handleClose} size="small">
						<CancelOutlined />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ py: 3 }}>
					<Grid2 container spacing={3}>
						{/* Company Info */}
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Typography variant="subtitle2" color="primary" mt={2}>
								Company Information
							</Typography>

							<TextField
								fullWidth
								label="GST Number"
								value={kyc.gstNumber}
								onChange={(e) => handleChange('gstNumber', e.target.value)}
								margin="normal"
								size="small"
							/>

							{/* Company Document Button */}
							<Box display="flex" alignItems="center" mt={2}>
								<input
									type="file"
									ref={companyFileRef}
									style={{ display: 'none' }}
									onChange={(e: any) =>
										handleChange('companyDocumentFile', e.target.files[0])
									}
								/>
								<Button
									startIcon={<UploadFile sx={{ color: 'red' }} />}
									onClick={() => companyFileRef.current?.click()}
									sx={{ mr: 2, textTransform: 'none', border: '2px dashed gray' }}
								>
									{kyc.companyDocumentFile
										? 'File Selected'
										: 'CompanyRegistrationDocument'}
								</Button>
								{kyc.companyDocumentFile && (
									<Typography>{kyc.companyDocumentFile.name}</Typography>
								)}
							</Box>

							{/* GST Document Button */}
							<Box display="flex" alignItems="center" mt={2}>
								<input
									type="file"
									ref={gstFileRef}
									style={{ display: 'none' }}
									onChange={(e: any) =>
										handleChange('gstDocumentFile', e.target.files[0])
									}
								/>
								<Button
									variant="outlined"
									startIcon={<UploadFile sx={{ color: 'red' }} />}
									onClick={() => gstFileRef.current?.click()}
									sx={{ mr: 2, textTransform: 'none', border: '2px dashed gray' }}
								>
									{kyc.gstDocumentFile ? 'File Selected' : 'Upload GST Document'}
								</Button>
								{kyc.gstDocumentFile && (
									<Typography>{kyc.gstDocumentFile.name}</Typography>
								)}
							</Box>
						</Grid2>

						{/* Bank Info */}
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Typography variant="subtitle2" color="primary" mt={2}>
								Bank Information
							</Typography>

							<TextField
								fullWidth
								label="Account Holder Name"
								value={kyc.accountHolder}
								onChange={(e) => handleChange('accountHolder', e.target.value)}
								margin="normal"
								size="small"
							/>
							<TextField
								fullWidth
								label="Account Number"
								value={kyc.accountNumber}
								onChange={(e) => handleChange('accountNumber', e.target.value)}
								margin="normal"
								size="small"
							/>
							<TextField
								fullWidth
								label="Account Type"
								value={kyc.accountType}
								onChange={(e) => handleChange('accountType', e.target.value)}
								margin="normal"
								size="small"
							/>
							<TextField
								fullWidth
								label="Bank Name"
								value={kyc.bankName}
								onChange={(e) => handleChange('bankName', e.target.value)}
								margin="normal"
								size="small"
							/>
							<TextField
								fullWidth
								label="Branch"
								value={kyc.branch}
								onChange={(e) => handleChange('branch', e.target.value)}
								margin="normal"
								size="small"
							/>
							<TextField
								fullWidth
								label="IFSC Code"
								value={kyc.ifscCode}
								onChange={(e) => handleChange('ifscCode', e.target.value)}
								margin="normal"
								size="small"
							/>
						</Grid2>
					</Grid2>
				</DialogContent>

				<DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
					<Button
						onClick={handleClose}
						variant="outlined"
						color="inherit"
						sx={{ borderRadius: 2, textTransform: 'none' }}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						disabled={loading}
						sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
					>
						{loading ? 'Saving...' : 'Save Changes'}
					</Button>
				</DialogActions>
			</motion.div>
		</Dialog>
	);
}
